import locales from '@locales';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { UserLocale } from '@models/user.model';

export const LocaleTypes = {
  SET_LOCALE: 'SET_LOCALE',
};

// Sprint 27, ISR-52. The GraphQL enum fix (backend `UserLocale` input DTOs) rejects out-of-enum
// values at the API boundary, but this reducer is reachable from any dispatch — including a
// stale session's persisted value or a future caller that forgets to validate — so it stays
// defensive on its own. Mirrors mobile's `AuthLocaleSync`'s `SUPPORTED.includes(...)` guard.
const SUPPORTED_LOCALES: string[] = Object.values(UserLocale);

export const setLocale = (locale: string) => ({
  type: LocaleTypes.SET_LOCALE,
  data: locale,
});

// Sprint 28, story-143, Decision 4 (docs/architecture.md "## Sprint 28"). The same
// device/browser-detection rule the initial state below already used, extracted so `logout`
// (the one Bucket 1 page reached from a Bucket-2-adjacent path) can re-run it explicitly rather
// than trusting the just-logged-out user's leftover `activeLocale` still sitting in
// localStorage. Not a new detection mechanism — the same rule, callable a second time.
export const detectDeviceLocale = (): string =>
  SUPPORTED_LOCALES.includes(navigator.language.split('-')[0])
    ? navigator.language.split('-')[0]
    : UserLocale.EN;

export type LocaleState = {
  activeLocale: string;
  locale: { [key: string]: any };
} & PersistPartial;

// INITIAL STATE
const initialLanguage: string = detectDeviceLocale();
const localeInitialState: LocaleState & PersistPartial = {
  activeLocale: initialLanguage,
  locale: {
    ...(locales as { [key: string]: any })[initialLanguage],
  },
  _persist: {
    version: 0,
    rehydrated: false,
  },
};

export type LocaleAction = {
  type: string;
  data: string;
};

const localeReducer = (state = localeInitialState, action: LocaleAction) => {
  switch (action.type) {
    case LocaleTypes.SET_LOCALE: {
      // Reject anything outside the known enum instead of setting `locale` to `undefined` —
      // an unguarded value here previously meant every subsequent `useTranslation()` call
      // (`locale[key]`) would throw and crash the session.
      if (!SUPPORTED_LOCALES.includes(action.data)) {
        return state;
      }
      return {
        ...state,
        activeLocale: action.data,
        locale: (locales as { [key: string]: any })[action.data],
      };
    }

    default:
      return state;
  }
};

export default localeReducer;
